import React, {useEffect} from 'react';
import {toast} from 'react-toastify';
import {z} from 'zod';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {DialogTitle} from '@headlessui/react';
import {getGetPaymentCardsQueryKey, useUpdatePaymentCard, type PaymentCardType, type UpdatePaymentCardBody} from 'api/generated';
import {Button, InputText, Modal} from 'components';
import {cacheUpdateItem} from 'utils';

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  line1: z.string().min(1, 'Address line 1 is required'),
  line2: z.string().optional(),
  postalCode: z.coerce.number().int().min(1, 'Zip/Postal Code is required'),
  state: z.string().min(1, 'State/Province is required'),
  city: z.string().min(1, 'City is required'),
  country: z.string().min(1, 'Country is required'),
});

interface IModalPaymentCardUpdateProps {
  visible: boolean;
  onClose: () => void;
  card: PaymentCardType | undefined;
}

const ModalPaymentCardUpdate = ({visible, onClose, card}: IModalPaymentCardUpdateProps) => {
  const updatePaymentCard = useUpdatePaymentCard();
  const {
    register,
    reset,
    handleSubmit,
    formState: {errors},
  } = useForm({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (visible && card) {
      const address = card.billingAddress;
      reset({
        name: address?.name ?? '',
        line1: address?.line1 ?? '',
        line2: address?.line2 ?? '',
        city: address?.city ?? '',
        state: address?.state ?? '',
        postalCode: address?.postalCode ?? undefined,
        country: address?.country ?? '',
      });
    }
  }, [visible, card]);

  const onSubmit = async (data: UpdatePaymentCardBody) => {
    if (card) {
      const {data: res} = await updatePaymentCard.mutateAsync({uid: card.uid, data});
      if (res.data) {
        onClose();
        toast.success('Payment card updated');
        cacheUpdateItem({
          queryKey: getGetPaymentCardsQueryKey(),
          data: res.data,
        });
      } else if (res.errors) {
        toast.error(res.errors[0].detail);
      }
    }
  };

  return (
    <Modal visible={visible} onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full">
        <DialogTitle as="h3" className="text-xl font-semibold text-gray-900 dark:text-white pb-5">
          Billing Address
        </DialogTitle>
        <InputText label="Name" {...register('name')} error={errors.name?.message} />
        <InputText label="Address line 1" {...register('line1')} error={errors.line1?.message} />
        <InputText label="Address line 2 (optional)" {...register('line2')} error={errors.line2?.message} />
        <div className="grid grid-cols-2 sm:gap-3">
          <div className="col-span-2 sm:col-span-1">
            <InputText label="City" {...register('city')} error={errors.city?.message} />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <InputText label="State/Province" {...register('state')} error={errors.state?.message} />
          </div>
        </div>
        <div className="grid grid-cols-2 sm:gap-3">
          <div className="col-span-2 sm:col-span-1">
            <InputText label="Country" {...register('country')} error={errors.country?.message} />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <InputText type="number" label="Zip/Postal Code" {...register('postalCode')} error={errors.postalCode?.message} />
          </div>
        </div>
        <div className="mt-5 w-full flex items-center justify-between">
          <div className="w-20">
            <Button text="Cancel" fullWidth size="extra-small" variant="secondary" disabled={updatePaymentCard.isPending} onClick={onClose} />
          </div>
          <div className="w-20">
            <Button type="submit" fullWidth text="Confirm" size="extra-small" loading={updatePaymentCard.isPending} />
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default React.memo(ModalPaymentCardUpdate);
